import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Users, Image, Wallet, ShieldAlert, Ban, CheckCircle, 
  XCircle, Trash2, Edit, Loader2, RefreshCw, Search,
  Activity, Clock, AlertTriangle, ShieldCheck, Eye, Globe, Copy
} from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useWalletSafe } from "@/hooks/useWalletSafe";
import { useWalletSupabaseSession } from "@/hooks/useWalletSupabaseSession";
import AppHeader from "@/components/AppHeader";
import Footer from "@/components/Footer";

import { FraudIdentityDetailDialog } from "@/components/admin/FraudIdentityDetailDialog";
import { OracleHealthMonitor } from "@/components/admin/OracleHealthMonitor";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type User = {
  id: string;
  wallet_address: string;
  display_name: string | null;
  avatar_url: string | null;
  is_banned: boolean | null;
  is_flagged: boolean | null;
  is_frozen: boolean | null;
  is_shadow_banned: boolean | null;
  rejected_nft_count: number | null;
  created_at: string;
};

type LensReport = {
  analyzed_at: string;
  duplicate_score: number;
  is_duplicate: boolean;
  web_detection?: {
    full_matches?: Array<{ url: string }>;
    partial_matches?: Array<{ url: string }>;
    pages_with_image?: Array<{ url: string; title: string }>;
    visually_similar?: Array<{ url: string }>;
    web_entities?: Array<{ description: string; score: number }>;
    best_guess_labels?: string[];
  };
  safe_search?: {
    adult: string;
    spoof: string;
    medical: string;
    violence: string;
    racy: string;
  };
  labels?: Array<{ description: string; score: number }>;
  counts?: {
    full_matches: number;
    partial_matches: number;
    pages_with_image: number;
    visually_similar: number;
    web_entities: number;
  };
  reasons?: string[];
};

type NFT = {
  id: string;
  name: string;
  image_url: string | null;
  owner_wallet: string;
  owner_name: string | null;
  creator_wallet: string | null;
  creator_name: string | null;
  price: number;
  current_price: number | null;
  sales_count: number;
  is_listed: boolean | null;
  created_at: string | null;
  image_verified: boolean | null;
  image_check_status: 'pending' | 'approved' | 'rejected' | null;
  is_wash_trade_detected: boolean | null;
  image_lens_report: LensReport | null;
};

type WithdrawalRequest = {
  id: string;
  user_id: string;
  wallet_address: string;
  amount: number;
  status: string;
  tx_signature: string | null;
  requested_at: string | null;
  processed_at: string | null;
};

type BannedUser = {
  id: string;
  user_id: string | null;
  wallet_address: string;
  reason: string | null;
  expires_at: string | null;
  created_at: string;
};

type AdminAction = {
  id: string;
  admin_wallet: string;
  action_type: string;
  target_id: string | null;
  target_type: string | null;
  notes: string | null;
  created_at: string;
};

type UserStrike = {
  id: string;
  user_id: string;
  wallet_address: string;
  display_name: string | null;
  strike_count: number;
  last_strike_at: string | null;
  last_strike_reason: string | null;
  is_banned: boolean;
  ban_expires_at: string | null;
  ban_reason: string | null;
  created_at: string;
  updated_at: string;
};

export default function Admin() {
  const navigate = useNavigate();
  const { connected, publicKey } = useWalletSafe();
  const { status: sessionStatus } = useWalletSupabaseSession();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("users");
  
  // Data states
  const [users, setUsers] = useState<User[]>([]);
  const [nfts, setNfts] = useState<NFT[]>([]);
  const [withdrawals, setWithdrawals] = useState<WithdrawalRequest[]>([]);
  const [bannedUsers, setBannedUsers] = useState<BannedUser[]>([]);
  const [adminActions, setAdminActions] = useState<AdminAction[]>([]);
  const [userStrikes, setUserStrikes] = useState<UserStrike[]>([]);
  const [fraudIdentities, setFraudIdentities] = useState<any[]>([]);
  const [whitelist, setWhitelist] = useState<any[]>([]);
  const [fraudSearch, setFraudSearch] = useState("");
  const [whitelistSearch, setWhitelistSearch] = useState("");
  const [newWhitelistWallet, setNewWhitelistWallet] = useState("");
  const [newWhitelistReason, setNewWhitelistReason] = useState("");
  
  // Search/filter states
  const [userSearch, setUserSearch] = useState("");
  const [nftSearch, setNftSearch] = useState("");
  const [withdrawalSearch, setWithdrawalSearch] = useState("");
  const [bannedSearch, setBannedSearch] = useState("");
  const [logSearch, setLogSearch] = useState("");
  const [strikeSearch, setStrikeSearch] = useState("");
  const [showFullWallets, setShowFullWallets] = useState(true);

  // Helper to display wallet
  const displayWallet = (wallet: string) => {
    if (showFullWallets) return wallet;
    return `${wallet.slice(0, 6)}...${wallet.slice(-4)}`;
  };

  // Copy wallet to clipboard
  const copyWallet = (wallet: string) => {
    navigator.clipboard.writeText(wallet);
    toast({ title: "Wallet copiato", description: wallet });
  };
  
  // Dialog states
  const [banDialogOpen, setBanDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [banReason, setBanReason] = useState("");
  const [banDuration, setBanDuration] = useState("permanent");

  // Fraud > Manual Wash-Unlock tool (admin)
  const [washDialogOpen, setWashDialogOpen] = useState(false);
  const [washNftIdInput, setWashNftIdInput] = useState("");
  const [washLookupLoading, setWashLookupLoading] = useState(false);
  const [washNft, setWashNft] = useState<Pick<
    NFT,
    "id" | "name" | "owner_wallet" | "is_listed" | "image_check_status" | "is_wash_trade_detected"
  > | null>(null);

  // Check admin access
  useEffect(() => {
    const checkAdmin = async () => {
      if (!connected || !publicKey) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      // Ensure auth session exists before role lookup
      if (sessionStatus !== "valid") {
        setIsAdmin(false);
        setLoading(sessionStatus === "syncing");
        return;
      }

      const { data: { session } } = await supabase.auth.getSession();
      const userId = session?.user?.id;
      if (!userId) {
        setIsAdmin(false);
        setLoading(false);
        return;
      }

      const { data: roleData, error } = await supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", userId)
        .eq("role", "admin")
        .maybeSingle();

      if (error) {
        console.error("Admin role check failed:", error);
      }

      setIsAdmin(!!roleData);
      setLoading(false);
    };

    checkAdmin();
  }, [connected, publicKey, sessionStatus]);

  // Fetch all data
  const fetchData = async () => {
    if (!isAdmin) return;

    // Fetch users
    const { data: usersData } = await supabase
      .from("profiles")
      .select("id, wallet_address, display_name, avatar_url, is_banned, is_flagged, is_frozen, is_shadow_banned, rejected_nft_count, created_at")
      .order("created_at", { ascending: false });
    setUsers(usersData || []);

    // Fetch NFTs
    const { data: nftsData } = await supabase
      .from("nfts")
      .select("id, name, image_url, owner_wallet, owner_name, creator_wallet, creator_name, price, current_price, sales_count, is_listed, created_at, image_verified, image_check_status, is_wash_trade_detected, image_lens_report")
      .order("created_at", { ascending: false });
    setNfts((nftsData || []) as NFT[]);

    // Fetch withdrawals
    const { data: withdrawalsData } = await supabase
      .from("withdrawal_requests")
      .select("*")
      .order("requested_at", { ascending: false });
    setWithdrawals(withdrawalsData || []);

    // Fetch banned users
    const { data: bannedData } = await supabase
      .from("banned_users")
      .select("*")
      .order("created_at", { ascending: false });
    setBannedUsers(bannedData || []);

    // Fetch admin actions
    const { data: actionsData } = await supabase
      .from("admin_actions")
      .select("*")
      .order("created_at", { ascending: false })
      .limit(100);
    setAdminActions(actionsData || []);

    // Fetch user strikes
    const { data: strikesData } = await supabase
      .from("user_strikes")
      .select("*")
      .order("strike_count", { ascending: false });
    
    // Enrich with display names
    if (strikesData) {
      const enrichedStrikes = await Promise.all(strikesData.map(async (s) => {
        const { data: profile } = await supabase
          .from("profiles")
          .select("display_name")
          .eq("id", s.user_id)
          .maybeSingle();
        return { ...s, display_name: profile?.display_name || null };
      }));
      setUserStrikes(enrichedStrikes);
    }

    // Fetch fraud identities with linked wallets AND their blocked NFTs
    const { data: fraudData } = await supabase
      .from("fraud_identities")
      .select("*")
      .order("risk_score", { ascending: false });
    
    if (fraudData) {
      const enrichedFraud = await Promise.all(fraudData.map(async (f) => {
        const { data: wallets } = await supabase
          .from("fraud_wallet_links")
          .select("wallet_address, confidence, is_primary")
          .eq("fraud_identity_id", f.id);

        const walletAddresses = wallets?.map(w => w.wallet_address) || [];
        
        // Fetch NFTs with wash_trade_detected for these wallets
        let blockedNfts: Array<{id: string; name: string; owner_wallet: string; is_wash_trade_detected: boolean}> = [];
        if (walletAddresses.length > 0) {
          const { data: nftData } = await supabase
            .from("nfts")
            .select("id, name, owner_wallet, is_wash_trade_detected")
            .in("owner_wallet", walletAddresses)
            .eq("is_wash_trade_detected", true);
          blockedNfts = nftData || [];
        }

        return { ...f, wallets: wallets || [], blockedNfts };
      }));
      setFraudIdentities(enrichedFraud);
    }

    // Fetch whitelist
    const { data: whitelistData } = await supabase
      .from("fraud_whitelist")
      .select("*")
      .order("created_at", { ascending: false });
    setWhitelist(whitelistData || []);
  };

  // Reset user strikes
  const handleResetStrikes = async (userId: string) => {
    const { error } = await supabase.rpc('reset_user_strikes', { p_user_id: userId });
    if (error) {
      toast({ title: "Errore nel reset degli strike", variant: "destructive" });
      return;
    }
    await logAction("reset_strikes", userId, "user", "Admin ha resettato gli strike");
    toast({ title: "Strike resettati con successo" });
    fetchData();
  };

  // Unban user via RPC
  const handleUnbanUserStrike = async (userId: string) => {
    const { error } = await supabase.rpc('unban_user', { p_user_id: userId });
    if (error) {
      toast({ title: "Errore nello sblocco utente", variant: "destructive" });
      return;
    }
    await logAction("unban_user", userId, "user", "Admin ha sbloccato manualmente l'utente");
    toast({ title: "Utente sbloccato con successo" });
    fetchData();
  };

  // Filter for strikes
  const filteredStrikes = userStrikes.filter(s =>
    s.wallet_address.toLowerCase().includes(strikeSearch.toLowerCase()) ||
    (s.display_name?.toLowerCase() || "").includes(strikeSearch.toLowerCase())
  );

  useEffect(() => {
    if (isAdmin) {
      fetchData();
    }
  }, [isAdmin]);

  // Real-time subscription for global coherence
  useEffect(() => {
    if (!isAdmin) return;

    const nftChannel = supabase
      .channel('admin-nft-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'nfts' }, () => {
        fetchData();
      })
      .subscribe();

    const profileChannel = supabase
      .channel('admin-profile-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'profiles' }, () => {
        fetchData();
      })
      .subscribe();

    const strikeChannel = supabase
      .channel('admin-strike-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'user_strikes' }, () => {
        fetchData();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(nftChannel);
      supabase.removeChannel(profileChannel);
      supabase.removeChannel(strikeChannel);
    };
  }, [isAdmin]);

  // Log admin action
  const logAction = async (actionType: string, targetId: string | null, targetType: string | null, notes: string | null) => {
    if (!publicKey) return;
    
    await supabase.from("admin_actions").insert({
      admin_wallet: publicKey.toBase58(),
      action_type: actionType,
      target_id: targetId,
      target_type: targetType,
      notes
    });
  };

  // Ban user
  const handleBanUser = async () => {
    if (!selectedUser) return;

    const expiresAt = banDuration === "permanent" 
      ? null 
      : new Date(Date.now() + parseInt(banDuration) * 24 * 60 * 60 * 1000).toISOString();

    // Insert into banned_users
    const { error: banError } = await supabase.from("banned_users").insert({
      user_id: selectedUser.id,
      wallet_address: selectedUser.wallet_address,
      reason: banReason,
      expires_at: expiresAt
    });

    if (banError) {
      toast({ title: "Errore nel ban utente", variant: "destructive" });
      return;
    }

    // Update profile
    await supabase.from("profiles").update({ is_banned: true }).eq("id", selectedUser.id);

    await logAction("ban_user", selectedUser.id, "user", `Motivo: ${banReason}, Durata: ${banDuration}`);
    
    toast({ title: "Utente bannato con successo" });
    setBanDialogOpen(false);
    setBanReason("");
    setSelectedUser(null);
    fetchData();
  };

  // Unban user
  const handleUnbanUser = async (user: User) => {
    await supabase.from("banned_users").delete().eq("wallet_address", user.wallet_address);
    await supabase.from("profiles").update({ is_banned: false }).eq("id", user.id);
    await logAction("unban_user", user.id, "user", null);
    
    toast({ title: "Utente sbannato con successo" });
    fetchData();
  };

  // Freeze user
  const handleFreezeUser = async (user: User) => {
    const { error } = await supabase.rpc('admin_freeze_user', { p_user_id: user.id });
    if (error) {
      toast({ title: "Errore nel freeze utente", variant: "destructive" });
      return;
    }
    await logAction("freeze_user", user.id, "user", "Admin ha congelato l'account");
    toast({ title: "Utente congelato con successo" });
    fetchData();
  };

  // Unfreeze user
  const handleUnfreezeUser = async (user: User) => {
    const { error } = await supabase.rpc('admin_unfreeze_user', { p_user_id: user.id });
    if (error) {
      toast({ title: "Errore nello scongelamento utente", variant: "destructive" });
      return;
    }
    await logAction("unfreeze_user", user.id, "user", "Admin ha scongelato l'account");
    toast({ title: "Utente scongelato con successo" });
    fetchData();
  };

  // Shadow ban user
  const handleShadowBanUser = async (user: User) => {
    const { error } = await supabase.rpc('admin_shadow_ban_user', { p_user_id: user.id });
    if (error) {
      toast({ title: "Errore nel shadow ban", variant: "destructive" });
      return;
    }
    await logAction("shadow_ban_user", user.id, "user", "Admin ha applicato shadow ban");
    toast({ title: "Shadow ban applicato con successo" });
    fetchData();
  };

  // Remove shadow ban
  const handleUnshadowBanUser = async (user: User) => {
    const { error } = await supabase.rpc('admin_unshadow_ban_user', { p_user_id: user.id });
    if (error) {
      toast({ title: "Errore nella rimozione shadow ban", variant: "destructive" });
      return;
    }
    await logAction("unshadow_ban_user", user.id, "user", "Admin ha rimosso shadow ban");
    toast({ title: "Shadow ban rimosso con successo" });
    fetchData();
  };

  // Force reject NFT
  const handleForceRejectNFT = async (nft: NFT) => {
    const { error } = await supabase.rpc('admin_force_reject_nft', { p_nft_id: nft.id });
    if (error) {
      toast({ title: "Errore nel rifiuto NFT", variant: "destructive" });
      return;
    }
    await logAction("force_reject_nft", nft.id, "nft", `Rifiutato forzatamente: ${nft.name}`);
    toast({ title: "NFT rifiutato con successo" });
    fetchData();
  };

  // Force approve NFT
  const handleForceApproveNFT = async (nft: NFT) => {
    const { error } = await supabase.rpc('admin_force_approve_nft', { p_nft_id: nft.id });
    if (error) {
      toast({ title: "Errore nell'approvazione NFT", variant: "destructive" });
      return;
    }
    await logAction("force_approve_nft", nft.id, "nft", `Approvato forzatamente: ${nft.name}`);
    toast({ title: "NFT approvato con successo" });
    fetchData();
  };

  // Clear wash trade flag
  const handleClearWashTrade = async (nft: NFT) => {
    const { error } = await supabase.rpc('admin_clear_wash_trade', { p_nft_id: nft.id });
    if (error) {
      toast({ title: "Errore nella rimozione flag wash trade", variant: "destructive" });
      return;
    }
    await logAction("clear_wash_trade", nft.id, "nft", `Rimosso flag wash trade: ${nft.name}`);
    toast({ title: "Flag wash trade rimosso" });
    fetchData();
  };

  // Fraud > Manual Wash-Unlock (by NFT id)
  const lookupWashNft = async () => {
    const nftId = washNftIdInput.trim();
    if (!nftId) {
      toast({ title: "Inserisci un NFT ID", variant: "destructive" });
      return;
    }

    setWashLookupLoading(true);
    try {
      const { data, error } = await supabase
        .from("nfts")
        .select("id, name, owner_wallet, is_listed, image_check_status, is_wash_trade_detected")
        .eq("id", nftId)
        .maybeSingle();

      if (error) throw error;

      if (!data) {
        setWashNft(null);
        toast({ title: "NFT non trovato", variant: "destructive" });
        return;
      }

      setWashNft(data as any);
    } catch (e) {
      console.error("[Admin/Frodi] lookupWashNft failed", e);
      toast({ title: "Errore nel recupero NFT", variant: "destructive" });
    } finally {
      setWashLookupLoading(false);
    }
  };

  // Delete NFT
  const handleDeleteNFT = async (nft: NFT) => {
    const { error } = await supabase.from("nfts").delete().eq("id", nft.id);
    
    if (error) {
      toast({ title: "Errore nell'eliminazione NFT", variant: "destructive" });
      return;
    }

    await logAction("delete_nft", nft.id, "nft", `Eliminato NFT: ${nft.name}`);
    toast({ title: "NFT eliminato con successo" });
    fetchData();
  };

  // Toggle NFT listing
  const handleToggleNFTListing = async (nft: NFT) => {
    const { error } = await supabase
      .from("nfts")
      .update({ is_listed: !nft.is_listed })
      .eq("id", nft.id);
    
    if (error) {
      toast({ title: "Errore nell'aggiornamento NFT", variant: "destructive" });
      return;
    }

    await logAction("toggle_nft_listing", nft.id, "nft", `Impostato is_listed a ${!nft.is_listed}`);
    toast({ title: `NFT ${nft.is_listed ? "rimosso dalla lista" : "listato"} con successo` });
    fetchData();
  };

  // Process withdrawal
  const handleProcessWithdrawal = async (withdrawal: WithdrawalRequest, action: "approve" | "reject") => {
    const newStatus = action === "approve" ? "processed" : "failed";
    
    const { error } = await supabase
      .from("withdrawal_requests")
      .update({ 
        status: newStatus,
        processed_at: new Date().toISOString()
      })
      .eq("id", withdrawal.id);
    
    if (error) {
      toast({ title: "Errore nell'elaborazione prelievo", variant: "destructive" });
      return;
    }

    await logAction(`${action}_withdrawal`, withdrawal.id, "withdrawal", `Importo: ${withdrawal.amount} SOL`);
    toast({ title: `Prelievo ${action === "approve" ? "approvato" : "rifiutato"} con successo` });
    fetchData();
  };

  // Filter functions
  const filteredUsers = users.filter(u => 
    u.wallet_address.toLowerCase().includes(userSearch.toLowerCase()) ||
    (u.display_name?.toLowerCase() || "").includes(userSearch.toLowerCase())
  );

  const filteredNFTs = nfts.filter(n => 
    n.name.toLowerCase().includes(nftSearch.toLowerCase()) ||
    n.owner_wallet.toLowerCase().includes(nftSearch.toLowerCase()) ||
    (n.owner_name?.toLowerCase() || "").includes(nftSearch.toLowerCase())
  );

  const filteredWithdrawals = withdrawals.filter(w =>
    w.wallet_address.toLowerCase().includes(withdrawalSearch.toLowerCase()) ||
    w.status.toLowerCase().includes(withdrawalSearch.toLowerCase())
  );

  const filteredBannedUsers = bannedUsers.filter(b =>
    b.wallet_address.toLowerCase().includes(bannedSearch.toLowerCase()) ||
    (b.reason?.toLowerCase() || "").includes(bannedSearch.toLowerCase())
  );

  const filteredAdminActions = adminActions.filter(a =>
    a.admin_wallet.toLowerCase().includes(logSearch.toLowerCase()) ||
    a.action_type.toLowerCase().includes(logSearch.toLowerCase()) ||
    (a.notes?.toLowerCase() || "").includes(logSearch.toLowerCase())
  );

  const filteredFraudIdentities = fraudIdentities.filter(f =>
    f.wallets?.some((w: any) => w.wallet_address.toLowerCase().includes(fraudSearch.toLowerCase())) ||
    f.status?.toLowerCase().includes(fraudSearch.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!connected) {
    return (
        <div className="min-h-screen bg-background flex flex-col">
        <AppHeader currentPage="admin" />
        <main className="flex-1 flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="p-8 text-center">
              <ShieldAlert className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
              <h2 className="text-xl font-bold mb-2">Connetti Wallet</h2>
              <p className="text-muted-foreground">Connetti il tuo wallet admin per accedere a questa pagina.</p>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  if (!isAdmin) {
    return (
        <div className="min-h-screen bg-background flex flex-col">
        <AppHeader currentPage="admin" />
        <main className="flex-1 flex items-center justify-center">
          <Card className="max-w-md">
            <CardContent className="p-8 text-center">
              <Ban className="w-16 h-16 mx-auto text-destructive mb-4" />
              <h2 className="text-xl font-bold mb-2">Accesso Negato</h2>
              <p className="text-muted-foreground">Non hai i permessi per accedere al pannello admin.</p>
              <Button className="mt-4" onClick={() => navigate("/")}>Torna alla Home</Button>
            </CardContent>
          </Card>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <AppHeader currentPage="admin" />
      
      <main className="flex-1 container mx-auto max-w-7xl px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground">Pannello Admin</h1>
            <p className="text-muted-foreground">Gestione utenti, NFT e operazioni piattaforma</p>
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Aggiorna
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Users className="w-5 h-5 text-primary" />
                <span className="text-2xl font-bold">{users.length}</span>
              </div>
              <p className="text-xs text-muted-foreground">Utenti Totali</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Image className="w-5 h-5 text-accent" />
                <span className="text-2xl font-bold">{nfts.length}</span>
              </div>
              <p className="text-xs text-muted-foreground">NFT Totali</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-green-500" />
                <span className="text-2xl font-bold">{withdrawals.filter(w => w.status === "pending").length}</span>
              </div>
              <p className="text-xs text-muted-foreground">Prelievi in Attesa</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Ban className="w-5 h-5 text-destructive" />
                <span className="text-2xl font-bold">{bannedUsers.length}</span>
              </div>
              <p className="text-xs text-muted-foreground">Utenti Bannati</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center gap-2">
                <Activity className="w-5 h-5 text-yellow-500" />
                <span className="text-2xl font-bold">{adminActions.length}</span>
              </div>
              <p className="text-xs text-muted-foreground">Azioni Admin</p>
            </CardContent>
          </Card>
        </div>

        {/* Oracle Health Monitor */}
        <div className="mb-6">
          <OracleHealthMonitor />
        </div>

        {/* Tabs */}
        {/* Calculate badge counts */}
        {(() => {
          const pendingVerification = nfts.filter(n => n.image_check_status === 'pending').length;
          const pendingLens = nfts.filter(n => n.image_lens_report && !n.image_verified && n.image_check_status !== 'rejected').length;
          const pendingWithdrawals = withdrawals.filter(w => w.status === 'pending').length;
          const activeStrikes = userStrikes.filter(s => s.strike_count > 0).length;
          const warnedFraud = fraudIdentities.filter(f => f.status === 'warned' || f.status === 'restricted').length;
          const flaggedUsers = users.filter(u => u.is_flagged || u.is_frozen || u.is_shadow_banned).length;

          return (
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className="overflow-x-auto pb-2 mb-4 -mx-4 px-4">
                <TabsList className="inline-flex w-max min-w-full md:w-full gap-1">
                  <TabsTrigger value="users" className="relative whitespace-nowrap text-xs md:text-sm px-2 md:px-3">
                    Utenti
                    {flaggedUsers > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] flex items-center justify-center text-[9px] font-bold bg-yellow-500 text-white rounded-full px-0.5">
                        {flaggedUsers}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="nfts" className="whitespace-nowrap text-xs md:text-sm px-2 md:px-3">NFT</TabsTrigger>
                  <TabsTrigger value="verification" className="relative whitespace-nowrap text-xs md:text-sm px-2 md:px-3">
                    Verifica
                    {pendingVerification > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] flex items-center justify-center text-[9px] font-bold bg-primary text-white rounded-full px-0.5">
                        {pendingVerification}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="lens" className="relative whitespace-nowrap text-xs md:text-sm px-2 md:px-3">
                    Lens
                    {pendingLens > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] flex items-center justify-center text-[9px] font-bold bg-orange-500 text-white rounded-full px-0.5">
                        {pendingLens}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="withdrawals" className="relative whitespace-nowrap text-xs md:text-sm px-2 md:px-3">
                    Prelievi
                    {pendingWithdrawals > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] flex items-center justify-center text-[9px] font-bold bg-green-500 text-white rounded-full px-0.5">
                        {pendingWithdrawals}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="strikes" className="relative whitespace-nowrap text-xs md:text-sm px-2 md:px-3">
                    Strike
                    {activeStrikes > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] flex items-center justify-center text-[9px] font-bold bg-red-500 text-white rounded-full px-0.5">
                        {activeStrikes}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="fraud" className="relative whitespace-nowrap text-xs md:text-sm px-2 md:px-3">
                    Frodi
                    {warnedFraud > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] flex items-center justify-center text-[9px] font-bold bg-destructive text-white rounded-full px-0.5">
                        {warnedFraud}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="banned" className="relative whitespace-nowrap text-xs md:text-sm px-2 md:px-3">
                    Bannati
                    {bannedUsers.length > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] flex items-center justify-center text-[9px] font-bold bg-muted-foreground text-white rounded-full px-0.5">
                        {bannedUsers.length}
                      </span>
                    )}
                  </TabsTrigger>
                  <TabsTrigger value="logs" className="whitespace-nowrap text-xs md:text-sm px-2 md:px-3">Log</TabsTrigger>
                  <TabsTrigger value="whitelist" className="relative whitespace-nowrap text-xs md:text-sm px-2 md:px-3">
                    Whitelist
                    {whitelist.length > 0 && (
                      <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] flex items-center justify-center text-[9px] font-bold bg-green-500 text-white rounded-full px-0.5">
                        {whitelist.length}
                      </span>
                    )}
                  </TabsTrigger>
                </TabsList>
              </div>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card>
              <CardHeader>
                <CardTitle>Gestione Utenti</CardTitle>
                <div className="relative max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Cerca per wallet o nome..."
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
                <Button
                  variant={showFullWallets ? "default" : "outline"}
                  size="sm"
                  onClick={() => setShowFullWallets(!showFullWallets)}
                  className="flex items-center gap-2"
                >
                  <Eye className="w-4 h-4" />
                  {showFullWallets ? "Nascondi Wallet" : "Mostra Wallet Completi"}
                </Button>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Avatar</TableHead>
                        <TableHead>Wallet</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead>Stato</TableHead>
                        <TableHead>NFT Rifiutati</TableHead>
                        <TableHead>Iscritto</TableHead>
                        <TableHead>Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredUsers.map((user) => (
                        <TableRow key={user.id}>
                          <TableCell>
                            {user.avatar_url ? (
                              <img src={user.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                                <Users className="w-4 h-4 text-muted-foreground" />
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-xs">
                            <div className="flex items-center gap-1">
                              <div className="min-w-0 max-w-[12rem] sm:max-w-[18rem] overflow-x-auto">
                                <span className="whitespace-nowrap">{displayWallet(user.wallet_address)}</span>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 flex-shrink-0"
                                onClick={() => copyWallet(user.wallet_address)}
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell>{user.display_name || "-"}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {user.is_frozen ? (
                                <Badge variant="destructive">Congelato</Badge>
                              ) : user.is_banned ? (
                                <Badge variant="destructive">Bannato</Badge>
                              ) : user.is_flagged ? (
                                <Badge className="bg-yellow-500/20 text-yellow-500 border-yellow-500/30">Segnalato</Badge>
                              ) : (
                                <Badge variant="secondary">Attivo</Badge>
                              )}
                              {user.is_shadow_banned && (
                                <Badge variant="outline" className="text-xs">Shadow</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            {user.rejected_nft_count || 0}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {new Date(user.created_at).toLocaleDateString('it-IT')}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {user.is_frozen ? (
                                <Button size="sm" variant="outline" onClick={() => handleUnfreezeUser(user)}>
                                  Scongela
                                </Button>
                              ) : (
                                <Button size="sm" variant="destructive" onClick={() => handleFreezeUser(user)}>
                                  Congela
                                </Button>
                              )}
                              {user.is_shadow_banned ? (
                                <Button size="sm" variant="outline" onClick={() => handleUnshadowBanUser(user)}>
                                  Rimuovi Shadow
                                </Button>
                              ) : (
                                <Button size="sm" variant="secondary" onClick={() => handleShadowBanUser(user)}>
                                  Shadow
                                </Button>
                              )}
                              {user.is_banned ? (
                                <Button size="sm" variant="outline" onClick={() => handleUnbanUser(user)}>
                                  Sbanna
                                </Button>
                              ) : (
                              <Dialog open={banDialogOpen && selectedUser?.id === user.id} onOpenChange={(open) => {
                                setBanDialogOpen(open);
                                if (open) setSelectedUser(user);
                              }}>
                                <DialogTrigger asChild>
                                  <Button size="sm" variant="destructive">
                                    <Ban className="w-3 h-3 mr-1" />
                                    Banna
                                  </Button>
                                </DialogTrigger>
                                <DialogContent>
                                  <DialogHeader>
                                    <DialogTitle>Banna Utente</DialogTitle>
                                  </DialogHeader>
                                  <div className="space-y-4">
                                    <div>
                                      <p className="text-sm text-muted-foreground mb-2">
                                        Wallet: {user.wallet_address.slice(0, 8)}...{user.wallet_address.slice(-6)}
                                      </p>
                                    </div>
                                    <div>
                                      <label className="text-sm font-medium">Motivo</label>
                                      <Textarea
                                        value={banReason}
                                        onChange={(e) => setBanReason(e.target.value)}
                                        placeholder="Inserisci il motivo del ban..."
                                      />
                                    </div>
                                    <div>
                                      <label className="text-sm font-medium">Durata</label>
                                      <Select value={banDuration} onValueChange={setBanDuration}>
                                        <SelectTrigger>
                                          <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="1">1 Giorno</SelectItem>
                                          <SelectItem value="7">7 Giorni</SelectItem>
                                          <SelectItem value="30">30 Giorni</SelectItem>
                                          <SelectItem value="permanent">Permanente</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    </div>
                                  </div>
                                  <DialogFooter>
                                    <DialogClose asChild>
                                      <Button variant="outline">Annulla</Button>
                                    </DialogClose>
                                    <Button variant="destructive" onClick={handleBanUser}>
                                      Conferma Ban
                                    </Button>
                                  </DialogFooter>
                                </DialogContent>
                              </Dialog>
                            )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* NFTs Tab */}
          <TabsContent value="nfts">
            <Card>
              <CardHeader>
                <CardTitle>Gestione NFT</CardTitle>
                <div className="relative max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Cerca per nome o proprietario..."
                    value={nftSearch}
                    onChange={(e) => setNftSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Immagine</TableHead>
                        <TableHead>Nome</TableHead>
                        <TableHead>Creatore</TableHead>
                        <TableHead>Proprietario</TableHead>
                        <TableHead>Prezzo</TableHead>
                        <TableHead>Vendite</TableHead>
                        <TableHead>Listato</TableHead>
                        <TableHead>Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredNFTs.map((nft) => (
                        <TableRow key={nft.id}>
                          <TableCell>
                            {nft.image_url ? (
                              <img src={nft.image_url} alt="" className="w-10 h-10 rounded object-cover" />
                            ) : (
                              <div className="w-10 h-10 rounded bg-muted flex items-center justify-center">
                                <Image className="w-5 h-5 text-muted-foreground" />
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="font-medium">{nft.name}</TableCell>
                          <TableCell className="text-xs">
                            <div className="flex items-center gap-1">
                              <div className="min-w-0 max-w-[10rem] overflow-x-auto">
                                <span className={nft.creator_name ? "whitespace-nowrap" : "font-mono whitespace-nowrap"}>
                                  {nft.creator_name || (nft.creator_wallet ? displayWallet(nft.creator_wallet) : "Unknown")}
                                </span>
                              </div>
                              {nft.creator_wallet && (
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-6 w-6 flex-shrink-0"
                                  onClick={() => copyWallet(nft.creator_wallet!)}
                                >
                                  <Copy className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-xs">
                            <div className="flex items-center gap-1">
                              <div className="min-w-0 max-w-[10rem] overflow-x-auto">
                                <span className={nft.owner_name ? "whitespace-nowrap" : "font-mono whitespace-nowrap"}>
                                  {nft.owner_name || displayWallet(nft.owner_wallet)}
                                </span>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 flex-shrink-0"
                                onClick={() => copyWallet(nft.owner_wallet)}
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell>${(nft.current_price || nft.price).toFixed(2)} USD</TableCell>
                          <TableCell>{nft.sales_count}</TableCell>
                          <TableCell>
                            <Badge variant={nft.is_listed ? "default" : "secondary"}>
                              {nft.is_listed ? "Listato" : "Non Listato"}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2 flex-wrap">
                              {nft.is_wash_trade_detected && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleClearWashTrade(nft)}
                                >
                                  Sblocca wash
                                </Button>
                              )}
                              <Button size="sm" variant="outline" onClick={() => handleToggleNFTListing(nft)}>
                                {nft.is_listed ? "Rimuovi" : "Lista"}
                              </Button>
                              <Button size="sm" variant="destructive" onClick={() => handleDeleteNFT(nft)}>
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Image Verification Tab */}
          <TabsContent value="verification">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Verifica Immagini
                </CardTitle>
                <CardDescription>Rivedi e verifica le immagini NFT</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {nfts.filter(n => n.image_check_status === 'pending' || n.image_check_status === 'rejected').map((nft) => (
                    <Card key={nft.id} className="overflow-hidden">
                      <div className="aspect-square relative">
                        {nft.image_url ? (
                          <img src={nft.image_url} alt={nft.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full bg-muted flex items-center justify-center">
                            <Image className="w-8 h-8 text-muted-foreground" />
                          </div>
                        )}
                        <Badge 
                          className="absolute top-2 right-2" 
                          variant={nft.image_check_status === 'pending' ? 'secondary' : 'destructive'}
                        >
                          {nft.image_check_status === 'pending' ? 'In Attesa' : 'Rifiutato'}
                        </Badge>
                      </div>
                      <CardContent className="p-3 space-y-2">
                        <p className="font-medium text-sm truncate">{nft.name}</p>
                        <p className="text-xs text-muted-foreground truncate flex items-center gap-1">
                          <span>{nft.owner_name || displayWallet(nft.owner_wallet)}</span>
                          <Button variant="ghost" size="icon" className="h-5 w-5" onClick={() => copyWallet(nft.owner_wallet)}>
                            <Copy className="w-2.5 h-2.5" />
                          </Button>
                        </p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="flex-1"
                            onClick={async () => {
                              await supabase.from("nfts").update({ 
                                image_verified: true, 
                                image_check_status: 'approved' 
                              }).eq("id", nft.id);
                              await logAction("approve_nft_image", nft.id, "nft", `Approvata immagine per ${nft.name}`);
                              toast({ title: "Immagine approvata" });
                              fetchData();
                            }}
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Approva
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="flex-1"
                            onClick={async () => {
                              await supabase.from("nfts").update({ 
                                image_verified: false, 
                                image_check_status: 'rejected' 
                              }).eq("id", nft.id);
                              await logAction("reject_nft_image", nft.id, "nft", `Rifiutata immagine per ${nft.name}`);
                              toast({ title: "Immagine rifiutata" });
                              fetchData();
                            }}
                          >
                            <XCircle className="w-3 h-3 mr-1" />
                            Rifiuta
                          </Button>
                        </div>
                        {nft.image_url && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full"
                            onClick={async () => {
                              toast({ title: "Ri-verifica immagine in corso..." });
                              await supabase.from("nfts").update({ 
                                image_check_status: 'pending' 
                              }).eq("id", nft.id);
                              await supabase.functions.invoke('verify-nft-image', {
                                body: { nft_id: nft.id, image_url: nft.image_url }
                              });
                              await logAction("reverify_nft_image", nft.id, "nft", `Ri-avviata verifica per ${nft.name}`);
                              fetchData();
                            }}
                          >
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Ri-verifica
                          </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
                {nfts.filter(n => n.image_check_status === 'pending' || n.image_check_status === 'rejected').length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    <ShieldCheck className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Nessuna immagine in attesa di verifica o rifiutata</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Lens Verification Tab */}
          <TabsContent value="lens">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Globe className="w-5 h-5" />
                  Verifica Lens (Reverse Image Search)
                </CardTitle>
                <CardDescription>Report dettagliati delle verifiche immagini con Google Vision</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {nfts.filter(n => n.image_lens_report).map((nft) => (
                    <Card key={nft.id} className="overflow-hidden">
                      <div className="flex gap-4 p-4">
                        <div className="w-20 h-20 shrink-0">
                          {nft.image_url ? (
                            <img src={nft.image_url} alt={nft.name} className="w-full h-full object-cover rounded" />
                          ) : (
                            <div className="w-full h-full bg-muted rounded flex items-center justify-center">
                              <Image className="w-6 h-6 text-muted-foreground" />
                            </div>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between mb-2">
                            <h4 className="font-medium truncate">{nft.name}</h4>
                            <Badge variant={nft.image_lens_report?.is_duplicate ? "destructive" : "default"}>
                              {nft.image_lens_report?.is_duplicate ? "Duplicato" : "Originale"}
                            </Badge>
                          </div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                            <div className="bg-muted/50 p-2 rounded">
                              <p className="text-muted-foreground">Score</p>
                              <p className="font-bold">{nft.image_lens_report?.duplicate_score || 0}</p>
                            </div>
                            <div className="bg-muted/50 p-2 rounded">
                              <p className="text-muted-foreground">Match Esatti</p>
                              <p className="font-bold">{nft.image_lens_report?.counts?.full_matches || 0}</p>
                            </div>
                            <div className="bg-muted/50 p-2 rounded">
                              <p className="text-muted-foreground">Match Parziali</p>
                              <p className="font-bold">{nft.image_lens_report?.counts?.partial_matches || 0}</p>
                            </div>
                            <div className="bg-muted/50 p-2 rounded">
                              <p className="text-muted-foreground">Pagine Web</p>
                              <p className="font-bold">{nft.image_lens_report?.counts?.pages_with_image || 0}</p>
                            </div>
                          </div>
                          {nft.image_lens_report?.reasons && nft.image_lens_report.reasons.length > 0 && (
                            <p className="text-xs text-destructive mt-2">{nft.image_lens_report.reasons.join(', ')}</p>
                          )}
                          <div className="flex gap-2 mt-3">
                            <Button size="sm" onClick={() => handleForceApproveNFT(nft)}>
                              <CheckCircle className="w-3 h-3 mr-1" /> Approva
                            </Button>
                            <Button size="sm" variant="destructive" onClick={() => handleForceRejectNFT(nft)}>
                              <XCircle className="w-3 h-3 mr-1" /> Rifiuta
                            </Button>
                            <Button size="sm" variant="outline" onClick={async () => {
                              toast({ title: "Ri-verifica in corso..." });
                              await supabase.from("nfts").update({ image_check_status: 'pending' }).eq("id", nft.id);
                              await supabase.functions.invoke('verify-nft-image', { body: { nft_id: nft.id, image_url: nft.image_url } });
                              fetchData();
                            }}>
                              <RefreshCw className="w-3 h-3 mr-1" /> Ri-verifica
                            </Button>
                          </div>
                        </div>
                      </div>
                    </Card>
                  ))}
                  {nfts.filter(n => n.image_lens_report).length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                      <Globe className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>Nessun report Lens disponibile</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Strikes Tab */}
          <TabsContent value="strikes">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertTriangle className="w-5 h-5 text-yellow-500" />
                  Gestione Strike
                </CardTitle>
                <CardDescription>Monitora e gestisci strike utenti e auto-ban</CardDescription>
                <div className="relative max-w-sm mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Cerca per wallet o nome..."
                    value={strikeSearch}
                    onChange={(e) => setStrikeSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Wallet</TableHead>
                      <TableHead>Nome</TableHead>
                      <TableHead>Strike</TableHead>
                      <TableHead>Stato</TableHead>
                      <TableHead>Ultimo Strike</TableHead>
                      <TableHead>Azioni</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredStrikes.map((strike) => (
                      <TableRow key={strike.id}>
                        <TableCell className="font-mono text-xs">
                          <div className="flex items-center gap-1">
                            <div className="min-w-0 max-w-[12rem] sm:max-w-[18rem] overflow-x-auto">
                              <span className="whitespace-nowrap">{displayWallet(strike.wallet_address)}</span>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 flex-shrink-0"
                              onClick={() => copyWallet(strike.wallet_address)}
                            >
                              <Copy className="w-3 h-3" />
                            </Button>
                          </div>
                        </TableCell>
                        <TableCell>{strike.display_name || "-"}</TableCell>
                        <TableCell>
                          <Badge variant={strike.strike_count >= 3 ? "destructive" : strike.strike_count >= 2 ? "secondary" : "outline"}>
                            {strike.strike_count} strike{strike.strike_count !== 1 ? "" : ""}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          {strike.is_banned ? (
                            <Badge variant="destructive">Bannato</Badge>
                          ) : (
                            <Badge variant="secondary">Attivo</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {strike.last_strike_at ? new Date(strike.last_strike_at).toLocaleDateString('it-IT') : "-"}
                        </TableCell>
                        <TableCell className="space-x-2">
                          <Button size="sm" variant="outline" onClick={() => handleResetStrikes(strike.user_id)}>
                            <RefreshCw className="w-3 h-3 mr-1" />
                            Reset
                          </Button>
                          {strike.is_banned && (
                            <Button size="sm" variant="secondary" onClick={() => handleUnbanUserStrike(strike.user_id)}>
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Sblocca
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {filteredStrikes.length === 0 && (
                  <div className="text-center py-12 text-muted-foreground">
                    <ShieldCheck className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Nessun utente con strike</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Withdrawals Tab */}
          <TabsContent value="withdrawals">
            <Card>
              <CardHeader>
                <CardTitle>Richieste di Prelievo</CardTitle>
                <CardDescription>Rivedi e processa le richieste di prelievo degli utenti</CardDescription>
                <div className="relative max-w-sm mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Cerca per wallet o stato..."
                    value={withdrawalSearch}
                    onChange={(e) => setWithdrawalSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Wallet</TableHead>
                        <TableHead>Importo</TableHead>
                        <TableHead>Stato</TableHead>
                        <TableHead>Richiesto</TableHead>
                        <TableHead>Processato</TableHead>
                        <TableHead>Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredWithdrawals.map((w) => (
                        <TableRow key={w.id}>
                          <TableCell className="font-mono text-xs">
                            <div className="flex items-center gap-1">
                              <div className="min-w-0 max-w-[12rem] sm:max-w-[18rem] overflow-x-auto">
                                <span className="whitespace-nowrap">{displayWallet(w.wallet_address)}</span>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 flex-shrink-0"
                                onClick={() => copyWallet(w.wallet_address)}
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="font-medium">{w.amount} SOL</TableCell>
                          <TableCell>
                            <Badge 
                              variant={
                                w.status === "pending" ? "secondary" : 
                                w.status === "processed" ? "default" : "destructive"
                              }
                            >
                              {w.status === "pending" ? "In Attesa" : 
                               w.status === "processed" ? "Processato" : "Fallito"}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {w.requested_at ? new Date(w.requested_at).toLocaleString('it-IT') : "-"}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {w.processed_at ? new Date(w.processed_at).toLocaleString('it-IT') : "-"}
                          </TableCell>
                          <TableCell>
                            {w.status === "pending" && (
                              <div className="flex gap-2">
                                <Button size="sm" variant="default" onClick={() => handleProcessWithdrawal(w, "approve")}>
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Approva
                                </Button>
                                <Button size="sm" variant="destructive" onClick={() => handleProcessWithdrawal(w, "reject")}>
                                  <XCircle className="w-3 h-3 mr-1" />
                                  Rifiuta
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Fraud Identities Tab */}
          <TabsContent value="fraud">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldAlert className="w-5 h-5 text-destructive" />
                  Identità Fraudolente
                </CardTitle>
                <CardDescription>Gestisci identità fraudolente, wallet collegati e asset congelati</CardDescription>
                
                {/* Info box per spiegare le azioni */}
                <div className="mt-3 p-3 bg-muted/50 rounded-lg border border-border/50 text-sm space-y-2">
                  <div className="flex items-start gap-2">
                    <span className="font-semibold text-destructive">🚫 Ban (Congela):</span>
                    <span className="text-muted-foreground">Blocco esplicito - l'utente SA di essere bannato e vede errori quando prova a tradare.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-semibold text-yellow-500">👻 Shadow Ban:</span>
                    <span className="text-muted-foreground">Blocco invisibile - l'utente NON sa di essere bannato, i suoi NFT sono nascosti agli altri ma visibili a lui.</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="font-semibold text-green-500">✅ Reset Strike:</span>
                    <span className="text-muted-foreground">Pulisce gli strike e riporta lo status a "clean" (solo per warned/restricted).</span>
                  </div>
                </div>

                <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                  <div className="relative max-w-sm w-full">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="Cerca per wallet o stato..."
                      value={fraudSearch}
                      onChange={(e) => setFraudSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>

                  <Dialog open={washDialogOpen} onOpenChange={(o) => {
                    setWashDialogOpen(o);
                    if (!o) {
                      setWashNft(null);
                      setWashNftIdInput("");
                    }
                  }}>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-2">
                        <RefreshCw className="w-4 h-4" />
                        Sblocca Wash (NFT)
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg">
                      <DialogHeader>
                        <DialogTitle>Sblocca manualmente Wash Detected</DialogTitle>
                      </DialogHeader>

                      <div className="space-y-3">
                        <div className="space-y-2">
                          <p className="text-sm text-muted-foreground">
                            Inserisci l’ID dell’NFT: controlliamo lo stato e, se “wash detected”, puoi rimuovere il flag.
                          </p>
                          <div className="flex gap-2">
                            <Input
                              placeholder="NFT ID (UUID)"
                              value={washNftIdInput}
                              onChange={(e) => setWashNftIdInput(e.target.value)}
                            />
                            <Button
                              variant="secondary"
                              onClick={lookupWashNft}
                              disabled={washLookupLoading}
                            >
                              {washLookupLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Controlla"}
                            </Button>
                          </div>
                        </div>

                        {washNft && (
                          <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0">
                                <p className="text-sm font-medium truncate">{washNft.name}</p>
                                <p className="text-xs text-muted-foreground font-mono break-all">{washNft.id}</p>
                              </div>
                              <Badge variant={washNft.is_wash_trade_detected ? "destructive" : "outline"}>
                                {washNft.is_wash_trade_detected ? "wash detected" : "ok"}
                              </Badge>
                            </div>

                            <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
                              <div>
                                <span className="font-medium text-foreground">Listed:</span> {String(!!washNft.is_listed)}
                              </div>
                              <div>
                                <span className="font-medium text-foreground">Image:</span> {washNft.image_check_status ?? "-"}
                              </div>
                            </div>

                            <div className="flex flex-wrap gap-2 pt-1">
                              <Button
                                variant="destructive"
                                size="sm"
                                disabled={!washNft.is_wash_trade_detected}
                                onClick={async () => {
                                  await handleClearWashTrade(washNft as unknown as NFT);
                                  await lookupWashNft();
                                }}
                              >
                                Rimuovi flag Wash
                              </Button>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => copyWallet(washNft.owner_wallet)}
                              >
                                <Copy className="w-3 h-3 mr-1" />
                                Copia owner
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>

                      <DialogFooter>
                        <DialogClose asChild>
                          <Button variant="outline">Chiudi</Button>
                        </DialogClose>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>ID Identità</TableHead>
                        <TableHead>Wallet Collegati</TableHead>
                        <TableHead>NFT Bloccati (Wash)</TableHead>
                        <TableHead>Stato</TableHead>
                        <TableHead>Strike</TableHead>
                        <TableHead>Punteggio Rischio</TableHead>
                        <TableHead>Prima Attività</TableHead>
                        <TableHead>Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredFraudIdentities.map((fraud) => (
                        <TableRow key={fraud.id}>
                          <TableCell className="font-mono text-xs">
                            {fraud.id.slice(0, 8)}...
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                                {fraud.wallets?.map((w: any) => (
                                  <div key={w.wallet_address} className="flex items-center gap-1">
                                    <div className="min-w-0 max-w-[14rem] overflow-x-auto">
                                      <span className="font-mono text-xs whitespace-nowrap">
                                        {displayWallet(w.wallet_address)}
                                      </span>
                                    </div>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-5 w-5 flex-shrink-0"
                                      onClick={() => copyWallet(w.wallet_address)}
                                    >
                                      <Copy className="w-2.5 h-2.5" />
                                    </Button>
                                  {w.is_primary && (
                                    <Badge variant="outline" className="text-[10px]">Principale</Badge>
                                  )}
                                  <span className="text-[10px] text-muted-foreground">
                                    ({w.confidence}%)
                                  </span>
                                </div>
                              ))}
                            </div>
                          </TableCell>
                          {/* NFT Bloccati (Wash) column */}
                          <TableCell>
                            {fraud.blockedNfts?.length > 0 ? (
                              <div className="flex flex-col gap-1.5 max-h-32 overflow-y-auto">
                                {fraud.blockedNfts.map((nft: any) => (
                                  <div key={nft.id} className="flex items-center gap-1 p-1.5 bg-destructive/10 border border-destructive/30 rounded text-xs">
                                    <div className="flex-1 min-w-0">
                                      <p className="font-medium truncate">{nft.name}</p>
                                      <p className="font-mono text-[10px] text-muted-foreground truncate">{nft.id.slice(0, 12)}...</p>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 px-2 text-[10px] text-green-500 hover:bg-green-500/10"
                                      onClick={async () => {
                                        const { error } = await supabase.rpc('admin_clear_wash_trade', { p_nft_id: nft.id });
                                        if (error) {
                                          toast({ title: "Errore", variant: "destructive" });
                                          return;
                                        }
                                        await logAction("clear_wash_trade", nft.id, "nft", `Sblocco wash: ${nft.name}`);
                                        toast({ title: `Wash rimosso: ${nft.name}` });
                                        fetchData();
                                      }}
                                    >
                                      <RefreshCw className="w-3 h-3 mr-0.5" />
                                      Sblocca
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </TableCell>
                          <TableCell>
                            <Badge 
                              variant={
                                fraud.status === 'banned' ? 'destructive' :
                                fraud.status === 'restricted' ? 'secondary' :
                                fraud.status === 'warned' ? 'outline' : 'default'
                              }
                              className={
                                fraud.status === 'restricted' ? 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30' :
                                fraud.status === 'warned' ? 'bg-orange-500/20 text-orange-500 border-orange-500/30' : ''
                              }
                            >
                              {fraud.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-center font-bold">
                            <span className={fraud.strikes >= 3 ? 'text-destructive' : fraud.strikes >= 2 ? 'text-yellow-500' : ''}>
                              {fraud.strikes}/3
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <div className="w-16 h-2 bg-muted rounded-full overflow-hidden">
                                <div 
                                  className={`h-full ${
                                    fraud.risk_score >= 70 ? 'bg-destructive' :
                                    fraud.risk_score >= 40 ? 'bg-yellow-500' : 'bg-green-500'
                                  }`}
                                  style={{ width: `${fraud.risk_score}%` }}
                                />
                              </div>
                              <span className="text-xs">{fraud.risk_score}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {fraud.first_seen ? new Date(fraud.first_seen).toLocaleDateString() : '-'}
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-1 flex-wrap">
                              {/* Reset strikes - show for anyone with strikes > 0 or warned/restricted status */}
                              {(fraud.strikes > 0 || fraud.status === 'warned' || fraud.status === 'restricted') && fraud.status !== 'banned' && (
                                <Button 
                                  size="sm" 
                                  variant="outline"
                                  className="border-green-500/50 text-green-500 hover:bg-green-500/10"
                                  onClick={async () => {
                                    const { error } = await supabase.rpc('admin_unban_fraud_identity', {
                                      p_identity_id: fraud.id,
                                      p_reason: 'Reset strike manuale admin'
                                    });
                                    if (error) {
                                      toast({ title: "Errore nel reset strike", variant: "destructive" });
                                      return;
                                    }
                                    await logAction("fraud_reset_strikes", fraud.id, "fraud_identity", "Reset strike manuale");
                                    toast({ title: "Strike resettati - Identità pulita" });
                                    fetchData();
                                  }}
                                >
                                  <RefreshCw className="w-3 h-3 mr-1" />
                                  Reset
                                </Button>
                              )}
                              {fraud.status !== 'banned' ? (
                                <Button 
                                  size="sm" 
                                  variant="destructive"
                                  onClick={async () => {
                                    const { error } = await supabase.rpc('admin_ban_fraud_identity', {
                                      p_identity_id: fraud.id,
                                      p_reason: 'Ban manuale admin'
                                    });
                                    if (error) {
                                      toast({ title: "Errore nel ban identità", variant: "destructive" });
                                      return;
                                    }
                                    await logAction("fraud_ban", fraud.id, "fraud_identity", "Ban manuale");
                                    toast({ title: "Identità bannata" });
                                    fetchData();
                                  }}
                                >
                                  <Ban className="w-3 h-3 mr-1" />
                                  Banna
                                </Button>
                              ) : (
                                <>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    className="border-green-500/50 text-green-500 hover:bg-green-500/10"
                                    onClick={async () => {
                                      // Reset strikes and unban
                                      const { error } = await supabase.rpc('admin_unban_fraud_identity', {
                                        p_identity_id: fraud.id,
                                        p_reason: 'Riammissione manuale admin - strike resettati'
                                      });
                                      if (error) {
                                        toast({ title: "Errore nella riammissione", variant: "destructive" });
                                        return;
                                      }
                                      await logAction("fraud_readmit", fraud.id, "fraud_identity", "Riammissione completa");
                                      toast({ title: "Utente riammesso - Strike e ban rimossi" });
                                      fetchData();
                                    }}
                                  >
                                    <RefreshCw className="w-3 h-3 mr-1" />
                                    Riammetti
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline"
                                    onClick={async () => {
                                      const { error } = await supabase.rpc('admin_unban_fraud_identity', {
                                        p_identity_id: fraud.id,
                                        p_reason: 'Sblocco manuale admin'
                                      });
                                      if (error) {
                                        toast({ title: "Errore nello sblocco identità", variant: "destructive" });
                                        return;
                                      }
                                      await logAction("fraud_unban", fraud.id, "fraud_identity", "Sblocco manuale");
                                      toast({ title: "Identità sbloccata" });
                                      fetchData();
                                    }}
                                  >
                                    <CheckCircle className="w-3 h-3 mr-1" />
                                    Sblocca
                                  </Button>
                                </>
                              )}
                              <FraudIdentityDetailDialog fraud={fraud} />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                      {filteredFraudIdentities.length === 0 && (
                        <TableRow>
                          <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                            Nessuna identità fraudolenta trovata
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Banned Users Tab */}
          <TabsContent value="banned">
            <Card>
              <CardHeader>
                <CardTitle>Utenti Bannati</CardTitle>
                <CardDescription>Visualizza e gestisci gli utenti bannati</CardDescription>
                <div className="relative max-w-sm mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Cerca per wallet o motivo..."
                    value={bannedSearch}
                    onChange={(e) => setBannedSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Wallet</TableHead>
                        <TableHead>Motivo</TableHead>
                        <TableHead>Bannato il</TableHead>
                        <TableHead>Scadenza</TableHead>
                        <TableHead>Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredBannedUsers.map((ban) => (
                        <TableRow key={ban.id}>
                          <TableCell className="font-mono text-xs">
                            {ban.wallet_address.slice(0, 6)}...{ban.wallet_address.slice(-4)}
                          </TableCell>
                          <TableCell className="max-w-xs truncate">{ban.reason || "-"}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {new Date(ban.created_at).toLocaleString('it-IT')}
                          </TableCell>
                          <TableCell className="text-xs">
                            {ban.expires_at ? new Date(ban.expires_at).toLocaleString('it-IT') : "Permanente"}
                          </TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={async () => {
                                await supabase.from("banned_users").delete().eq("id", ban.id);
                                if (ban.user_id) {
                                  await supabase.from("profiles").update({ is_banned: false }).eq("id", ban.user_id);
                                }
                                await logAction("unban_user", ban.user_id || ban.wallet_address, "user", null);
                                toast({ title: "Utente sbannato" });
                                fetchData();
                              }}
                            >
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Sbanna
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Activity Log Tab */}
          <TabsContent value="logs">
            <Card>
              <CardHeader>
                <CardTitle>Log Attività Admin</CardTitle>
                <CardDescription>Azioni admin recenti sulla piattaforma</CardDescription>
                <div className="relative max-w-sm mt-2">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Cerca per admin, azione o note..."
                    value={logSearch}
                    onChange={(e) => setLogSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Admin</TableHead>
                        <TableHead>Azione</TableHead>
                        <TableHead>Target</TableHead>
                        <TableHead>Note</TableHead>
                        <TableHead>Data/Ora</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredAdminActions.map((action) => (
                        <TableRow key={action.id}>
                          <TableCell className="font-mono text-xs">
                            {action.admin_wallet.slice(0, 4)}...{action.admin_wallet.slice(-4)}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{action.action_type}</Badge>
                          </TableCell>
                          <TableCell className="text-xs">
                            {action.target_type}: {action.target_id?.slice(0, 8) || "-"}
                          </TableCell>
                          <TableCell className="max-w-xs truncate text-xs text-muted-foreground">
                            {action.notes || "-"}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {new Date(action.created_at).toLocaleString('it-IT')}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Whitelist Tab */}
          <TabsContent value="whitelist">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <ShieldCheck className="w-5 h-5 text-green-500" />
                  Gestione Whitelist
                </CardTitle>
                <CardDescription>Wallet esenti dai controlli anti-frode</CardDescription>
                
                {/* Add new wallet form */}
                <div className="mt-4 p-4 bg-muted/50 rounded-lg border border-border/50 space-y-3">
                  <p className="font-semibold text-sm">Aggiungi wallet alla whitelist</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <Input
                      placeholder="Wallet address completo..."
                      value={newWhitelistWallet}
                      onChange={(e) => setNewWhitelistWallet(e.target.value)}
                      className="font-mono text-sm flex-1"
                    />
                    <Input
                      placeholder="Motivo (opzionale)..."
                      value={newWhitelistReason}
                      onChange={(e) => setNewWhitelistReason(e.target.value)}
                      className="flex-1"
                    />
                    <Button 
                      onClick={async () => {
                        if (!newWhitelistWallet.trim()) {
                          toast({ title: "Inserisci un wallet", variant: "destructive" });
                          return;
                        }
                        const { error } = await supabase.from("fraud_whitelist").insert({
                          wallet_address: newWhitelistWallet.trim(),
                          reason: newWhitelistReason.trim() || null,
                          added_by: publicKey?.toBase58() || "admin"
                        });
                        if (error) {
                          toast({ title: "Errore nell'aggiunta", description: error.message, variant: "destructive" });
                          return;
                        }
                        await logAction("add_whitelist", newWhitelistWallet.trim(), "wallet", newWhitelistReason || "Aggiunto alla whitelist");
                        toast({ title: "Wallet aggiunto alla whitelist" });
                        setNewWhitelistWallet("");
                        setNewWhitelistReason("");
                        fetchData();
                      }}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Aggiungi
                    </Button>
                  </div>
                </div>

                <div className="relative max-w-sm mt-4">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                  <Input
                    placeholder="Cerca per wallet..."
                    value={whitelistSearch}
                    onChange={(e) => setWhitelistSearch(e.target.value)}
                    className="pl-9"
                  />
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Wallet</TableHead>
                        <TableHead>Motivo</TableHead>
                        <TableHead>Aggiunto da</TableHead>
                        <TableHead>Data</TableHead>
                        <TableHead>Scadenza</TableHead>
                        <TableHead>Azioni</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {whitelist
                        .filter(w => w.wallet_address.toLowerCase().includes(whitelistSearch.toLowerCase()))
                        .map((w) => (
                        <TableRow key={w.id}>
                          <TableCell className="font-mono text-xs">
                            <div className="flex items-center gap-1">
                              <div className="min-w-0 max-w-[12rem] sm:max-w-[18rem] overflow-x-auto">
                                <span className="whitespace-nowrap">{displayWallet(w.wallet_address)}</span>
                              </div>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-6 w-6 flex-shrink-0"
                                onClick={() => copyWallet(w.wallet_address)}
                              >
                                <Copy className="w-3 h-3" />
                              </Button>
                            </div>
                          </TableCell>
                          <TableCell className="text-sm max-w-xs truncate">
                            {w.reason || "-"}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {w.added_by ? displayWallet(w.added_by) : "-"}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {new Date(w.created_at).toLocaleDateString('it-IT')}
                          </TableCell>
                          <TableCell className="text-xs">
                            {w.expires_at ? (
                              <Badge variant={new Date(w.expires_at) < new Date() ? "destructive" : "secondary"}>
                                {new Date(w.expires_at).toLocaleDateString('it-IT')}
                              </Badge>
                            ) : (
                              <Badge variant="outline">Permanente</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              onClick={async () => {
                                const { error } = await supabase.from("fraud_whitelist").delete().eq("id", w.id);
                                if (error) {
                                  toast({ title: "Errore nella rimozione", variant: "destructive" });
                                  return;
                                }
                                await logAction("remove_whitelist", w.wallet_address, "wallet", "Rimosso dalla whitelist");
                                toast({ title: "Wallet rimosso dalla whitelist" });
                                fetchData();
                              }}
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Rimuovi
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                  {whitelist.length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                      <ShieldCheck className="w-12 h-12 mx-auto mb-4 opacity-30" />
                      <p>Nessun wallet nella whitelist</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
          );
        })()}
      </main>

      <Footer />
    </div>
  );
}